import { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Plus, ChevronRight, Copy, Check, Lightbulb, Zap } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { T } from '../lib/tokens';
import { primaryRole } from '../lib/rbac';

interface Props { user: AuthUser }
interface Session { id:string; user_id:string; role:string; school_id:string|null; locale:'ms'|'en'; started_at:string; last_message_at:string; message_count:number; expires_at:string; }
interface PersistedMessage { id:string; session_id:string; role:'user'|'assistant'|'tool'; content:string|null; tool_name:string|null; tool_input:string|null; tool_output_preview:string|null; tokens_used:number; created_at:string; user_feedback:'POSITIVE'|'NEGATIVE'|null; is_flagged:boolean; }
interface SendResponse { assistantText:string; toolCalls:unknown[]; tokensUsed:number; usage:{ hourly:number; daily:number; hourlyLimit:number; dailyLimit:number }; sessionMessageCount:number; }
interface Usage { hourly:number; daily:number; hourlyLimit:number; dailyLimit:number }

const C = T.color;

// ── Markdown renderer ─────────────────────────────────────────────────────────
// Converts common Markdown to JSX without a library dependency.
function renderMarkdown(text: string): JSX.Element {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // H2
    if (line.startsWith('## ')) {
      elements.push(
        <h3 key={key++} style={{ fontFamily: T.font.serif, fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: '18px 0 10px', paddingBottom: '6px', borderBottom: '1.5px solid #E2E8F0' }}>
          {inlineMarkdown(line.slice(3))}
        </h3>
      );
      i++; continue;
    }

    // H3
    if (line.startsWith('### ')) {
      elements.push(
        <h4 key={key++} style={{ fontFamily: T.font.serif, fontSize: '14px', fontWeight: 700, color: '#1E293B', margin: '14px 0 8px' }}>
          {inlineMarkdown(line.slice(4))}
        </h4>
      );
      i++; continue;
    }

    // Blockquote
    if (line.startsWith('> ')) {
      elements.push(
        <div key={key++} style={{ borderLeft: '3px solid #F7D000', paddingLeft: '12px', margin: '10px 0', color: '#64748B', fontSize: '13px', fontStyle: 'italic' }}>
          {inlineMarkdown(line.slice(2))}
        </div>
      );
      i++; continue;
    }

    // Table
    if (line.includes('|') && line.trim().startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && (lines[i] ?? '').includes('|') && (lines[i] ?? '').trim().startsWith('|')) {
        tableLines.push(lines[i] ?? '');
        i++;
      }
      const [headerLine, , ...dataLines] = tableLines; // skip separator line
      if (headerLine) {
        const headers = headerLine.split('|').filter(h => h.trim()).map(h => h.trim());
        elements.push(
          <div key={key++} style={{ overflowX: 'auto', margin: '12px 0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  {headers.map((h, hi) => (
                    <th key={hi} style={{ padding: '8px 12px', textAlign: 'left', background: '#F8FAFC', borderBottom: '2px solid #E2E8F0', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {inlineMarkdown(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataLines.map((dl, dli) => {
                  const cells = dl.split('|').filter(c => c.trim()).map(c => c.trim());
                  return (
                    <tr key={dli} style={{ background: dli % 2 === 0 ? '#FFFFFF' : '#FAFAFA' }}>
                      {cells.map((cell, ci) => (
                        <td key={ci} style={{ padding: '8px 12px', borderBottom: '1px solid #F1F5F9', color: '#374151', verticalAlign: 'middle' }}>
                          {inlineMarkdown(cell)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Ordered list
    if (/^\d+\.\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? '')) {
        listItems.push((lines[i] ?? '').replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={key++} style={{ paddingLeft: '20px', margin: '8px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {listItems.map((item, ii) => (
            <li key={ii} style={{ fontSize: '13.5px', lineHeight: 1.6, color: '#374151' }}>{inlineMarkdown(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Bullet list (-, •, *)
    if (/^[-•*]\s/.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^[-•*]\s/.test(lines[i] ?? '')) {
        listItems.push((lines[i] ?? '').replace(/^[-•*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={key++} style={{ paddingLeft: '18px', margin: '8px 0', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {listItems.map((item, ii) => (
            <li key={ii} style={{ fontSize: '13.5px', lineHeight: 1.6, color: '#374151', listStyleType: 'disc' }}>{inlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Normal paragraph
    elements.push(
      <p key={key++} style={{ fontSize: '13.5px', lineHeight: 1.7, color: '#374151', margin: '6px 0' }}>
        {inlineMarkdown(line)}
      </p>
    );
    i++;
  }

  return <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{elements}</div>;
}

// Process inline markdown: **bold**, *italic*, `code`, emoji pass-through
function inlineMarkdown(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  // Bold
  const boldRe  = /\*\*(.+?)\*\*/g;
  // Italic
  const italRe  = /\*(.+?)\*/g;
  // Code
  const codeRe  = /`(.+?)`/g;

  let combined = text;
  let match: RegExpExecArray | null;
  const segments: Array<{ start: number; end: number; type: string; content: string }> = [];

  for (const re of [boldRe, italRe, codeRe]) {
    re.lastIndex = 0;
    while ((match = re.exec(text)) !== null) {
      segments.push({ start: match.index, end: match.index + match[0].length, type: re === boldRe ? 'bold' : re === italRe ? 'italic' : 'code', content: match[1] ?? '' });
    }
  }

  if (segments.length === 0) return [text];

  segments.sort((a, b) => a.start - b.start);
  let cursor = 0;
  let ki = 0;
  for (const seg of segments) {
    if (seg.start < cursor) continue;
    if (seg.start > cursor) parts.push(text.slice(cursor, seg.start));
    if (seg.type === 'bold')   parts.push(<strong key={ki++} style={{ fontWeight: 700, color: '#0F172A' }}>{seg.content}</strong>);
    if (seg.type === 'italic') parts.push(<em key={ki++} style={{ fontStyle: 'italic' }}>{seg.content}</em>);
    if (seg.type === 'code')   parts.push(<code key={ki++} style={{ background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '4px', padding: '1px 6px', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: '#0F172A' }}>{seg.content}</code>);
    cursor = seg.end;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

// ── Suggestions per role ──────────────────────────────────────────────────────
const SUGGESTIONS: Record<string, { ms:string; en:string }[]> = {
  teacher: [
    { ms:'Pelajar mana yang berisiko bulan ini?',            en:'Which students are at risk this month?' },
    { ms:'Tunjukkan trend kehadiran 3 bulan terakhir.',      en:'Show attendance trends for the past 3 months.' },
    { ms:'Apakah gred terbaik dan terendah dalam Matematik?',en:'What are the top and bottom grades in Maths?' },
    { ms:'Jadual waktu T4A untuk minggu ini.',               en:'Show the Form 4A timetable for this week.' },
  ],
  admin: [
    { ms:'Pelajar dengan kehadiran DAN gred rendah.',        en:'Students with both low attendance and low grades.' },
    { ms:'Tunjukkan 5 pelajar berisiko kritikal.',           en:'Show the 5 most at-risk students.' },
    { ms:'Ringkasan sekolah — pelajar, pendidik, kelas.',    en:'School overview — students, educators, classes.' },
    { ms:'Pendidik yang CPD di bawah 50 jam.',               en:'Educators with fewer than 50 CPD hours.' },
  ],
  parent: [
    { ms:'Gred anak saya dalam semua mata pelajaran.',       en:"My child's grades across all subjects." },
    { ms:'Berapa kali anak saya tidak hadir bulan ini?',     en:"How many times did my child miss school?" },
    { ms:'Jadual anak saya untuk minggu ini.',               en:"My child's timetable this week." },
    { ms:'Siapa guru kelas anak saya?',                      en:"Who is my child's class teacher?" },
  ],
  moe_admin: [
    { ms:'Kadar kehadiran mengikut daerah bulan ini.',       en:'Attendance rate by district this month.' },
    { ms:'Daerah mana yang paling rendah kadar kehadiran?',  en:'Which district has the lowest attendance rate?' },
    { ms:'Statistik enrolmen kebangsaan.',                   en:'National enrolment statistics.' },
    { ms:'Sekolah swasta dengan lesen hampir tamat.',        en:'Private schools with licenses expiring soon.' },
  ],
  dpe_officer: [
    { ms:'Sekolah swasta yang lesen tamat dalam 30 hari.',   en:'Private schools with licenses expiring in 30 days.' },
    { ms:'Sekolah yang gagal pemeriksaan terakhir.',         en:'Schools that failed their last inspection.' },
    { ms:'Senaraikan kelulusan guru yang tertangguh.',       en:'List pending teacher approvals.' },
    { ms:'Ringkasan pematuhan sekolah swasta.',              en:'Private school compliance summary.' },
  ],
  default: [
    { ms:'Tunjukkan ringkasan sekolah.',                     en:'Show school overview.' },
    { ms:'Pelajar mana yang berisiko hari ini?',             en:'Which students are at risk today?' },
    { ms:'Apakah laporan yang boleh saya jana?',             en:'What reports can I generate?' },
    { ms:'Tunjukkan trend kehadiran bulan ini.',             en:"Show this month's attendance trend." },
  ],
};

// ── Copy button ───────────────────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={copy}
      title={copied ? 'Copied!' : 'Copy response'}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px 6px', borderRadius: '5px', color: '#94A3B8', transition: 'all 80ms', display: 'flex', alignItems: 'center', gap: '3px' }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#F1F5F9'; (e.currentTarget as HTMLElement).style.color = '#374151'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; (e.currentTarget as HTMLElement).style.color = '#94A3B8'; }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      <span style={{ fontSize: '10px' }}>{copied ? 'Copied' : 'Copy'}</span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function EduBotPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  const role = primaryRole(user);
  usePageTitle('EduBot AI');

  const [sessions,      setSessions]      = useState<Session[]>([]);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [messages,      setMessages]      = useState<PersistedMessage[]>([]);
  const [input,         setInput]         = useState('');
  const [sending,       setSending]       = useState(false);
  const [starting,      setStarting]      = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [usage,         setUsage]         = useState<Usage | null>(null);
  const [locale,        setLocale]        = useState<'ms' | 'en'>(() => lang === 'en' ? 'en' : 'ms');
  const [pendingQ,      setPendingQ]      = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const textRef   = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { setLocale(lang === 'en' ? 'en' : 'ms'); }, [lang]);

  const suggestions = SUGGESTIONS[role] ?? SUGGESTIONS['default']!;

  const loadSessions = useCallback(async () => {
    try { setSessions(await apiGet<Session[]>('/api/edubot/sessions')); } catch { /* non-fatal */ }
  }, []);
  const loadUsage = useCallback(async () => {
    try { setUsage(await apiGet<Usage>('/api/edubot/usage')); } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { void loadSessions(); void loadUsage(); }, [loadSessions, loadUsage]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Once a session is active and a pending question is waiting, send it
  useEffect(() => {
    if (pendingQ && activeSession && !sending) {
      const q = pendingQ;
      setPendingQ(null);
      void handleSend(q);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingQ, activeSession]);

  async function openSession(s: Session) {
    setActiveSession(s); setMessages([]); setError(null);
    try {
      const res = await apiGet<{ session: Session; messages: PersistedMessage[] }>(`/api/edubot/sessions/${s.id}`);
      setActiveSession(res.session); setMessages(res.messages); setLocale(res.session.locale);
    } catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
  }

  async function startNewSession(withQuestion?: string) {
    setStarting(true); setError(null);
    try {
      const s = await apiPost<Session>('/api/edubot/sessions', { locale });
      setActiveSession(s); setMessages([]); setSessions(p => [s, ...p]);
      if (withQuestion) setPendingQ(withQuestion);
    } catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
    finally { setStarting(false); }
  }

  async function handleSend(text?: string) {
    const msg = (text ?? input).trim();
    if (!msg || !activeSession || sending) return;

    const optimistic: PersistedMessage = {
      id: `opt-${Date.now()}`, session_id: activeSession.id, role: 'user',
      content: msg, tool_name: null, tool_input: null, tool_output_preview: null,
      tokens_used: 0, created_at: new Date().toISOString(), user_feedback: null, is_flagged: false,
    };
    setMessages(p => [...p, optimistic]);
    setInput(''); setSending(true); setError(null);
    if (textRef.current) textRef.current.style.height = 'auto';

    try {
      const res = await apiPost<SendResponse>(`/api/edubot/sessions/${activeSession.id}/messages`, { message: msg, locale });
      const refreshed = await apiGet<{ session: Session; messages: PersistedMessage[] }>(`/api/edubot/sessions/${activeSession.id}`);
      setActiveSession(refreshed.session); setMessages(refreshed.messages); setUsage(res.usage);
    } catch (e) {
      setMessages(p => p.filter(m => m.id !== optimistic.id));
      setError(e instanceof Error ? e.message : 'Error sending message.');
    } finally { setSending(false); }
  }

  async function handleFeedback(id: string, fb: 'POSITIVE' | 'NEGATIVE') {
    try {
      await apiPost(`/api/edubot/messages/${id}/feedback`, { feedback: fb });
      setMessages(p => p.map(m => m.id === id ? { ...m, user_feedback: fb } : m));
    } catch { /* non-fatal */ }
  }

  function handleTextareaInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
  }

  const today = new Date().toDateString();
  const todaySessions  = sessions.filter(s => new Date(s.started_at).toDateString() === today);
  const olderSessions  = sessions.filter(s => new Date(s.started_at).toDateString() !== today);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '268px 1fr', height: 'calc(100vh - 92px)', background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>

      {/* ── Left sidebar ── */}
      <div style={{ background: '#0B1120', display: 'flex', flexDirection: 'column', borderRight: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '16px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #F7D000 0%, #C9A800 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(247,208,0,0.25)' }}>
              <Zap size={18} color="#0B1120" strokeWidth={2.5} />
            </div>
            <div>
              <p style={{ fontFamily: T.font.serif, fontSize: '15px', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.2 }}>EduBot</p>
              <p style={{ fontSize: '10px', color: '#475569', lineHeight: 1 }}>{E ? 'SERPS AI Assistant' : 'Pembantu AI SERPS'}</p>
            </div>
          </div>

          {/* Locale toggle */}
          <div style={{ display: 'flex', background: '#1E2A3B', borderRadius: '7px', padding: '2px', marginBottom: '10px' }}>
            {(['ms', 'en'] as const).map(l => (
              <button key={l} onClick={() => setLocale(l)} style={{ flex: 1, padding: '5px', background: locale === l ? '#F7D000' : 'transparent', color: locale === l ? '#0B1120' : '#475569', border: 'none', borderRadius: '5px', fontSize: '11px', fontWeight: locale === l ? 700 : 500, cursor: 'pointer', transition: 'all 120ms', fontFamily: T.font.sans, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {l === 'ms' ? 'BM' : 'EN'}
              </button>
            ))}
          </div>

          <button
            onClick={() => void startNewSession()}
            disabled={starting}
            style={{ width: '100%', background: 'linear-gradient(135deg, #F7D000 0%, #E8C200 100%)', color: '#0B1120', border: 'none', borderRadius: '8px', padding: '9px 12px', fontSize: '12px', fontWeight: 700, cursor: starting ? 'not-allowed' : 'pointer', fontFamily: T.font.sans, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: starting ? 0.7 : 1, boxShadow: '0 2px 8px rgba(247,208,0,0.2)', transition: 'all 120ms' }}
            onMouseEnter={e => { if (!starting) (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(247,208,0,0.3)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(247,208,0,0.2)'; }}
          >
            <Plus size={13} strokeWidth={2.5} />
            {starting ? (E ? 'Starting…' : 'Memulakan…') : (E ? 'New Chat' : 'Sembang Baharu')}
          </button>
        </div>

        {/* Session list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          {sessions.length === 0 && (
            <p style={{ fontSize: '12px', color: '#334155', textAlign: 'center', padding: '28px 12px', lineHeight: 1.6 }}>
              {E ? 'No chats yet.\nStart one above.' : 'Tiada sembang lagi.\nMulakan di atas.'}
            </p>
          )}
          {todaySessions.length > 0 && (
            <>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 6px 4px' }}>{E ? 'Today' : 'Hari Ini'}</p>
              {todaySessions.map(s => <SessionBtn key={s.id} session={s} active={activeSession?.id === s.id} onClick={() => void openSession(s)} lang={lang} />)}
            </>
          )}
          {olderSessions.length > 0 && (
            <>
              <p style={{ fontSize: '10px', fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '12px 6px 4px' }}>{E ? 'Earlier' : 'Terdahulu'}</p>
              {olderSessions.map(s => <SessionBtn key={s.id} session={s} active={activeSession?.id === s.id} onClick={() => void openSession(s)} lang={lang} />)}
            </>
          )}
        </div>

        {/* Usage */}
        {usage && (
          <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
              <p style={{ fontSize: '10px', color: '#475569' }}>{E ? 'Usage today' : 'Penggunaan hari ini'}</p>
              <p style={{ fontSize: '10px', color: '#475569' }}>{usage.daily}/{usage.dailyLimit}</p>
            </div>
            <div style={{ height: '3px', background: '#1E2A3B', borderRadius: '999px', overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'linear-gradient(90deg, #F7D000, #C9A800)', width: `${Math.min(100, Math.round((usage.daily / usage.dailyLimit) * 100))}%`, borderRadius: '999px', transition: 'width 300ms ease' }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Chat panel ── */}
      <div style={{ display: 'flex', flexDirection: 'column', background: '#FAFAFA', overflow: 'hidden' }}>

        {/* Chat header */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0, background: '#FFFFFF' }}>
          {activeSession ? (
            <>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E', flexShrink: 0, boxShadow: '0 0 0 2px #DCFCE7' }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', lineHeight: 1.2 }}>
                  {E ? 'Session active' : 'Sesi aktif'} · {activeSession.locale.toUpperCase()}
                </p>
                <p style={{ fontSize: '11px', color: '#94A3B8' }}>
                  {activeSession.message_count} {E ? 'messages' : 'mesej'} · {E ? 'expires' : 'tamat'} {new Date(activeSession.expires_at).toLocaleTimeString(E ? 'en-GB' : 'ms-BN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div style={{ fontSize: '11px', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Lightbulb size={12} />
                {E ? 'Shift+Enter for new line' : 'Shift+Enter untuk baris baharu'}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Zap size={16} color="#F7D000" />
              <p style={{ fontSize: '13px', color: '#64748B' }}>
                {E ? 'Select a session or start a new one' : 'Pilih sesi atau mulakan satu baharu'}
              </p>
            </div>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

          {/* Welcome screen */}
          {!activeSession && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 24px', maxWidth: '600px', margin: '0 auto', width: '100%' }}>
              <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: 'linear-gradient(135deg, #0B1120 0%, #1E2A3B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', boxShadow: '0 8px 24px rgba(11,17,32,0.15)' }}>
                <Zap size={32} color="#F7D000" strokeWidth={2} />
              </div>
              <h2 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: '#0F172A', marginBottom: '10px', letterSpacing: '-0.01em' }}>
                {E ? 'Welcome to EduBot' : 'Selamat Datang ke EduBot'}
              </h2>
              <p style={{ fontSize: '14px', color: '#64748B', maxWidth: '380px', lineHeight: 1.7, marginBottom: '28px' }}>
                {E
                  ? 'Ask me anything about your school data — attendance trends, student grades, at-risk analysis, educator CPD, and more.'
                  : 'Tanya saya apa-apa sahaja tentang data sekolah anda — trend kehadiran, gred pelajar, analisis risiko, CPD pendidik, dan banyak lagi.'}
              </p>

              <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                {E ? 'Try asking…' : 'Cuba tanya…'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', maxWidth: '520px', marginBottom: '28px' }}>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => void startNewSession(E ? s.en : s.ms)}
                    disabled={starting}
                    style={{ background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '999px', padding: '8px 16px', fontSize: '13px', color: '#374151', cursor: 'pointer', fontFamily: T.font.sans, transition: 'all 120ms', textAlign: 'left' }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = '#F7D000'; el.style.background = '#FFFDF0'; el.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = '#E2E8F0'; el.style.background = '#FFFFFF'; el.style.transform = 'none'; }}
                  >
                    {E ? s.en : s.ms}
                  </button>
                ))}
              </div>

              <button
                onClick={() => void startNewSession()}
                disabled={starting}
                style={{ background: 'linear-gradient(135deg, #F7D000 0%, #E8C200 100%)', color: '#0B1120', border: 'none', borderRadius: '10px', padding: '12px 28px', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: T.font.sans, boxShadow: '0 4px 14px rgba(247,208,0,0.3)', transition: 'all 120ms' }}
              >
                {starting ? (E ? 'Starting…' : 'Memulakan…') : (E ? '+ Start Conversation' : '+ Mulakan Perbualan')}
              </button>
            </div>
          )}

          {/* Empty session suggestions */}
          {activeSession && messages.length === 0 && !sending && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <p style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '16px', fontWeight: 500 }}>
                {E ? 'Start with a question:' : 'Mulakan dengan soalan:'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => void handleSend(E ? s.en : s.ms)}
                    style={{ background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '999px', padding: '8px 16px', fontSize: '12.5px', color: '#374151', cursor: 'pointer', fontFamily: T.font.sans, transition: 'all 120ms' }}
                    onMouseEnter={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = '#F7D000'; el.style.background = '#FFFDF0'; }}
                    onMouseLeave={e => { const el = e.currentTarget as HTMLElement; el.style.borderColor = '#E2E8F0'; el.style.background = '#FFFFFF'; }}
                  >
                    {E ? s.en : s.ms}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.filter(m => m.role !== 'tool').map(m => (
            <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              {m.role === 'user' ? (
                <div style={{ background: 'linear-gradient(135deg, #F7D000 0%, #E8C200 100%)', color: '#0F172A', padding: '11px 16px', borderRadius: '18px 18px 4px 18px', fontSize: '14px', lineHeight: 1.55, fontFamily: T.font.sans, whiteSpace: 'pre-wrap', boxShadow: '0 2px 8px rgba(247,208,0,0.2)' }}>
                  {m.content}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'linear-gradient(135deg, #0B1120 0%, #1E2A3B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 6px rgba(11,17,32,0.2)' }}>
                      <Zap size={14} color="#F7D000" strokeWidth={2.5} />
                    </div>
                    <div style={{ background: '#FFFFFF', color: '#0F172A', padding: '14px 18px', borderRadius: '4px 18px 18px 18px', border: '1px solid #E2E8F0', fontFamily: T.font.sans, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', maxWidth: '100%' }}>
                      {m.content ? renderMarkdown(m.content) : <span style={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '13px' }}>—</span>}
                    </div>
                  </div>

                  {/* Feedback row */}
                  <div style={{ display: 'flex', gap: '4px', paddingLeft: '40px', alignItems: 'center' }}>
                    {m.content && <CopyButton text={m.content} />}
                    <div style={{ width: '1px', height: '14px', background: '#E2E8F0' }} />
                    {(['POSITIVE', 'NEGATIVE'] as const).map(fb => (
                      <button key={fb} onClick={() => void handleFeedback(m.id, fb)} disabled={m.user_feedback !== null} title={fb === 'POSITIVE' ? (E ? 'Helpful' : 'Berguna') : (E ? 'Not helpful' : 'Tidak berguna')}
                        style={{ background: m.user_feedback === fb ? `${C.royal}25` : 'none', border: 'none', cursor: m.user_feedback ? 'default' : 'pointer', fontSize: '13px', padding: '2px 5px', borderRadius: '5px', transition: 'background 100ms', opacity: m.user_feedback && m.user_feedback !== fb ? 0.3 : 1 }}>
                        {fb === 'POSITIVE' ? '👍' : '👎'}
                      </button>
                    ))}
                    {m.tokens_used > 0 && <span style={{ fontSize: '10px', color: '#CBD5E1', marginLeft: '2px' }}>{m.tokens_used} tokens</span>}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* Thinking indicator */}
          {sending && (
            <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'linear-gradient(135deg, #0B1120 0%, #1E2A3B 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Zap size={14} color="#F7D000" strokeWidth={2.5} />
              </div>
              <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '4px 18px 18px 18px', padding: '14px 18px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#F7D000', animation: `pulse-dot 1.3s ${i * 0.2}s ease-in-out infinite` }} />
                  ))}
                  <span style={{ fontSize: '12px', color: '#94A3B8', marginLeft: '6px' }}>
                    {E ? 'Analysing data…' : 'Menganalisis data…'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        {activeSession && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E8F0', flexShrink: 0, background: '#FFFFFF' }}>
            {error && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px', fontSize: '13px', color: '#991B1B', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span>⚠</span>{error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                  ref={textRef}
                  value={input}
                  onChange={handleTextareaInput}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
                  placeholder={locale === 'ms' ? 'Taip soalan anda… (Shift+Enter untuk baris baharu)' : 'Type your question… (Shift+Enter for new line)'}
                  disabled={!activeSession || sending}
                  maxLength={4000}
                  rows={1}
                  style={{ width: '100%', background: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: '12px', padding: '12px 16px', fontSize: '14px', color: '#0F172A', fontFamily: T.font.sans, outline: 'none', resize: 'none', transition: 'border-color 120ms, box-shadow 120ms', lineHeight: 1.5, maxHeight: '160px', overflowY: 'auto' }}
                  onFocus={e => { e.target.style.borderColor = '#F7D000'; e.target.style.boxShadow = '0 0 0 3px rgba(247,208,0,0.12)'; }}
                  onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
              <button
                onClick={() => void handleSend()}
                disabled={!activeSession || sending || !input.trim()}
                title={E ? 'Send (Enter)' : 'Hantar (Enter)'}
                style={{ width: '44px', height: '44px', flexShrink: 0, background: input.trim() && !sending ? 'linear-gradient(135deg, #F7D000 0%, #E8C200 100%)' : '#F1F5F9', color: input.trim() && !sending ? '#0B1120' : '#94A3B8', border: 'none', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: input.trim() && !sending ? 'pointer' : 'not-allowed', transition: 'all 120ms', boxShadow: input.trim() && !sending ? '0 2px 8px rgba(247,208,0,0.25)' : 'none' }}
              >
                <Send size={17} strokeWidth={2.5} />
              </button>
            </div>
            <p style={{ fontSize: '11px', color: '#CBD5E1', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <span>{input.length}/4000</span>
              <span>{E ? 'EduBot can only access data within your role permissions.' : 'EduBot hanya mengakses data dalam had kebenaran peranan anda.'}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Session button ────────────────────────────────────────────────────────────
function SessionBtn({ session, active, onClick, lang }: { session: Session; active: boolean; onClick: () => void; lang: string }) {
  const E = lang === 'en';
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: active ? 'rgba(247,208,0,0.08)' : hovered ? 'rgba(255,255,255,0.03)' : 'transparent', border: 'none', borderRadius: '8px', cursor: 'pointer', transition: 'background 100ms', textAlign: 'left' }}
    >
      <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: active ? 'rgba(247,208,0,0.12)' : '#1E2A3B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Zap size={13} color={active ? '#F7D000' : '#334155'} strokeWidth={2} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '12px', fontWeight: active ? 600 : 400, color: active ? '#F8FAFC' : '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
          {session.message_count} {E ? 'messages' : 'mesej'} · {session.locale.toUpperCase()}
        </p>
        <p style={{ fontSize: '10px', color: '#334155', lineHeight: 1.2 }}>
          {new Date(session.started_at).toLocaleTimeString(E ? 'en-GB' : 'ms-BN', { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
      {active && <ChevronRight size={12} color="#F7D000" style={{ flexShrink: 0 }} />}
    </button>
  );
}
